import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type AdminTaskKind = "import" | "prefetch";
export type AdminTaskStatus = "idle" | "running" | "succeeded" | "failed";

export interface AdminTaskState {
  kind: AdminTaskKind;
  status: AdminTaskStatus;
  started_at: string | null;
  finished_at: string | null;
  progress: number;
  message: string;
  error: string | null;
  result: Record<string, unknown>;
}

export interface PrefetchParams {
  top_queries: number;
  top_tags: number;
  days: number;
}

export interface PrefetchPlan {
  params: PrefetchParams;
  sampled_queries: string[];
  sampled_tags: string[];
  total_unique_posts: number;
  previews_already_cached: number;
  previews_to_fetch: number;
  estimated_bytes: number;
}

async function fetchTasks(): Promise<AdminTaskState[]> {
  const res = await fetch("/api/v1/admin/tasks", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`tasks fetch failed: ${res.status}`);
  return (await res.json()).tasks;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `${path} failed: ${res.status}`);
  }
  return res.json();
}

export function useAdminTasks() {
  // Poll while anything is running, otherwise slow refresh.
  return useQuery<AdminTaskState[]>({
    queryKey: ["admin", "tasks"],
    queryFn: fetchTasks,
    refetchInterval: (q) => {
      const data = q.state.data;
      const anyRunning = data?.some((t) => t.status === "running") ?? false;
      return anyRunning ? 1500 : 15_000;
    },
    staleTime: 0,
  });
}

export function useStartImport() {
  const qc = useQueryClient();
  return useMutation<AdminTaskState, Error, boolean>({
    mutationFn: (force: boolean) =>
      postJson<AdminTaskState>("/api/v1/admin/tasks/import", { force }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "tasks"] }),
  });
}

export function usePrefetchPlan() {
  // Mutation rather than query — it's user-triggered and the result is a
  // snapshot to confirm against, not background-refreshable data.
  return useMutation({
    mutationFn: (params: PrefetchParams) =>
      postJson<PrefetchPlan>("/api/v1/admin/tasks/prefetch/plan", params),
  });
}

export function useStartPrefetch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: PrefetchParams) =>
      postJson<AdminTaskState>("/api/v1/admin/tasks/prefetch/execute", params),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "tasks"] }),
  });
}
